import { useState, useEffect } from 'react'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react'
import { useAuth } from '@/features/auth'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { Logo } from '@/components/Logo'

export const Route = createFileRoute('/signup')({
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    source: (search.source as string | undefined) ?? undefined,
    lead_id: (search.lead_id as string | undefined) ?? undefined,
    utm_source: (search.utm_source as string | undefined) ?? undefined,
    utm_medium: (search.utm_medium as string | undefined) ?? undefined,
    utm_campaign: (search.utm_campaign as string | undefined) ?? undefined,
    utm_term: (search.utm_term as string | undefined) ?? undefined,
    utm_content: (search.utm_content as string | undefined) ?? undefined,
  }),
})

const signupSchema = z.object({
  organization_name: z.string().trim().min(2, 'Informe o nome da organização').max(120),
  full_name: z.string().trim().min(2, 'Informe seu nome').max(100),
  email: z.string().email('E-mail inválido'),
  password: z
    .string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .regex(/[A-Z]/, 'Precisa de 1 letra maiúscula')
    .regex(/[0-9]/, 'Precisa de 1 número'),
  whatsapp: z.string().optional(),
  terms_accepted: z.boolean().refine((v) => v === true, 'É necessário aceitar os termos'),
  // Honeypot anti-bot (escondido; deve permanecer vazio)
  website: z.string().max(0).optional(),
})

type SignupFormData = z.infer<typeof signupSchema>

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ??
  'https://api.neurodivergente.secbra.in'

function collectUtms(search: ReturnType<typeof useSearch>) {
  const utm: Record<string, string> = {}
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const) {
    const value = search[key]
    if (typeof value === 'string' && value) utm[key.replace('utm_', '')] = value
  }
  return Object.keys(utm).length > 0 ? utm : undefined
}

interface SignupOk {
  organization_id: string
  organization_name: string
  trial_days: number
  next_step: 'confirm_email'
}

function SignupPage() {
  useDocumentTitle('Criar conta')
  const navigate = useNavigate()
  const search = Route.useSearch()
  const { session, loading } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState<SignupOk | null>(null)

  useEffect(() => {
    if (!loading && session) {
      navigate({ to: '/dashboard' })
    }
  }, [loading, session, navigate])

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      organization_name: '',
      full_name: '',
      email: '',
      password: '',
      whatsapp: '',
      terms_accepted: false,
      website: '',
    },
  })

  const onSubmit = async (data: SignupFormData) => {
    setError(null)
    setFieldErrors({})
    setIsSubmitting(true)

    try {
      const res = await fetch(`${API_URL}/api/public/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_name: data.organization_name,
          full_name: data.full_name,
          email: data.email.trim().toLowerCase(),
          password: data.password,
          whatsapp: data.whatsapp || undefined,
          terms_accepted: data.terms_accepted,
          website: data.website || '',
          source: (search.source as SignupInputSource) ?? 'landing',
          utm: collectUtms(search),
          lead_id: search.lead_id || undefined,
        }),
      })

      const body = await res.json().catch(() => ({}))

      if (res.ok) {
        setSuccess(body as SignupOk)
        return
      }

      if (res.status === 409) {
        setError(body?.message ?? 'Este e-mail já tem conta. Faça login.')
        return
      }
      if (res.status === 429) {
        setError('Muitas tentativas. Aguarde um minuto e tente de novo.')
        return
      }
      if (res.status === 503) {
        setError(body?.message ?? 'Cadastro temporariamente indisponível.')
        return
      }
      if (body?.errors && Array.isArray(body.errors)) {
        const fe: Record<string, string> = {}
        for (const issue of body.errors) {
          fe[issue.field] = issue.message
        }
        setFieldErrors(fe)
        setError('Verifique os campos destacados.')
        return
      }
      setError(body?.message ?? 'Não foi possível criar a conta. Tente novamente.')
    } catch {
      setError('Erro de conexão. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse">
          <Logo size={64} />
        </div>
      </div>
    )
  }

  if (session) return null

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center mb-4">
            <Logo size={64} />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-800 mb-2 font-poppins">
              Conta criada!
            </h1>
            <p className="text-gray-600 mb-1">
              A organização <strong>{success.organization_name}</strong> está pronta com
              trial de <strong>{success.trial_days} dias</strong>.
            </p>
            <p className="text-gray-600 mb-6">
              Enviamos um e-mail para você <strong>confirmar seu cadastro</strong>. Confira
              também a caixa de spam.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full py-3 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors"
            >
              Ir para o login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <Logo size={64} />
          </div>
          <h1 className="text-2xl font-semibold text-gray-800 mb-2 font-poppins">
            Crie sua conta grátis
          </h1>
          <p className="text-gray-600">
            14 dias de trial completo. Sem cartão de crédito.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Honeypot (invisível) */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" type="text" tabIndex={-1} autoComplete="off" {...form.register('website')} />
            </div>

            <div>
              <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 mb-2">
                Nome da organização
              </label>
              <input
                id="organization_name"
                type="text"
                placeholder="Clínica Exemplo"
                {...form.register('organization_name')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              />
              {(fieldErrors.organization_name || form.formState.errors.organization_name) && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.organization_name ?? form.formState.errors.organization_name?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                Seu nome
              </label>
              <input
                id="full_name"
                type="text"
                placeholder="Maria Silva"
                {...form.register('full_name')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              />
              {(fieldErrors.full_name || form.formState.errors.full_name) && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.full_name ?? form.formState.errors.full_name?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                E-mail profissional
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="voce@suaclinica.com.br"
                {...form.register('email')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              />
              {(fieldErrors.email || form.formState.errors.email) && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.email ?? form.formState.errors.email?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Mín. 8 caracteres, 1 maiúscula e 1 número"
                  {...form.register('password')}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {(fieldErrors.password || form.formState.errors.password) && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.password ?? form.formState.errors.password?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">
                WhatsApp <span className="text-gray-400">(opcional)</span>
              </label>
              <input
                id="whatsapp"
                type="tel"
                placeholder="(84) 99999-0000"
                {...form.register('whatsapp')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              />
            </div>

            <div>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  {...form.register('terms_accepted')}
                  className="mt-1 w-4 h-4 accent-teal-600"
                />
                <span className="text-sm text-gray-600">
                  Aceito os termos de uso e a política de privacidade (LGPD).
                </span>
              </label>
              {(fieldErrors.terms_accepted || form.formState.errors.terms_accepted) && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.terms_accepted ?? String(form.formState.errors.terms_accepted?.message ?? '')}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Criar conta grátis
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-500">
              Já tem conta?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
                Fazer login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

type SignupInputSource = 'direct' | 'landing' | 'indicacao' | 'prospeccao'
